import { createMuiTheme } from '@material-ui/core/styles'
import './Fonts.css'

const fontWeights = [
  100, // light
  200, // normal
  500, // medium
  700, // bold
  800, // extra bold
]
const maxWidth = `90%`

const fontSize = [
  12, //0
  14, //1
  15, //2
  20, //3
  22, //4
  25, //5
]
export const colors = {
  primary: { main: `#29323E`, light: `#FFFFFF`, dark: `#354052` },
  secondary: { light: `#428AA5`, main: `#023F73`, dark: `#023F73` },
  ternary: { main: `#0083e9`, light: `#E3E3E3`, dark: `#C0BFC0` },
  grayRgba: [
    `rgba(224, 224, 224, 1)`, // 0
    `rgba(0, 111, 179, 0.08)`, // 1
    `rgba(80,227,194,0)`, // 2
    `rgba(0, 0, 0, 0.08)`, //3
    `rgba(0, 0, 0, 0.12)`, //4
    `rgba(0, 0, 0, 0.14)`, //5
    `rgba(0, 0, 0, 0.26)`, //6
    `rgba(0, 0, 0, 0.38)`, //7
    `rgba(0,0,0,0.50)`, //8
    `rgba(0, 0, 0, 0.54)`, //9
  ],
  gray: [`#EFF3F6 `, `#F5F8FA`, `#DFE3E9`, `#7F8FA4`, `#494949`],
  error: { main: `#a91409`, light: `#EF3F44`, dark: `#BB6B76` },
  success: { main: `#007BFF`, info: `#0a6ab6`, warning: `#cc7a00` },
  verde: { main: `#28a745`, info: `#0a6ab6`, warning: `#cc7a00` },

}

const configFont = `Chilena`

const theme = createMuiTheme({
  palette: {
    primary: colors.primary,
    secondary: colors.secondary,
    letra: colors.gray[4],
    ternary: colors.ternary,
    verde: colors.verde,
    error: colors.error,
    gray: colors.gray,
    grayRgba: colors.grayRgba,
    success: colors.success,
    gris: colors.gray[2],
    grisDark: `#919191`,
    celeste: `rgba(215, 236, 255, 0.44)`,
    celesteDark: `#61B3FF`,
    azul: `#2B6CB2`,
    white: `#ffff`,
    text: {
      primary: colors.primary.light,
      secondary: colors.secondary.main,
      disabled: colors.grayRgba[7],
      hint: colors.grayRgba[7],
    },
    background: {
      default: colors.gray[0],
    },
    action: {
      active: colors.grayRgba[9],
      hover: colors.grayRgba[3],
      hoverOpacity: 0.08,
      selected: colors.grayRgba[5],
      disabled: colors.grayRgba[6],
      disabledBackground: colors.grayRgba[4],
    },
    type: `light`,
  },
  maxWidth,
  fontSize,
  fontWeight: fontWeights,
})
theme.typography = {
  ...theme.typography,
  htmlFontSize: 12,
  fontSize: fontSize[0],
  fontFamily: configFont,
  lineHeight: `1.5rem`,
  color: colors.gray[4],
  h1: {
    fontSize: fontSize[5],
    fontWeight: fontWeights[2],
    letterSpacing: -0.21,
    color: colors.primary.dark,
    textDecoration: `underline solid ${colors.primary.dark}`,
    paddingLeft: theme.spacing(4),
  },
  h2: {
    fontSize: fontSize[3],
    fontWeight: fontWeights[2],
    color: colors.primary.dark,
    [theme.breakpoints.down(`sm`)]: {
      fontSize: fontSize[3],
    },
  },
  h3: {
    fontSize: fontSize[2],
    fontWeight: fontWeights[2],
    color: colors.primary.dark,
  },
  h4: {
    fontSize: fontSize[2],
    fontWeight: fontWeights[1],
    fontFamily: configFont,
  },
  h5: {
    fontSize: fontSize[1],
    fontWeight: fontWeights[1],
    fontFamily: configFont,
  },
  h6: {
    fontSize: fontSize[1],
    fontWeight: fontWeights[1],
    fontFamily: configFont,
    letterSpacing: 0,
  },
  body1: {
    fontSize: fontSize[1],
    fontWeight: fontWeights[1],
    fontFamily: configFont,
    color: colors.primary.dark,
  },
  body2: {
    fontSize: fontSize[0],
    fontWeight: fontWeights[1],
    color: colors.primary.dark,
    fontFamily: configFont,
  },
  subtitle1: {
    fontSize: fontSize[2],
    fontWeight: fontWeights[1],
    color: colors.secondary.main,
    fontFamily: configFont,
  },
  subtitle2: {
    fontSize: fontSize[1],
    fontWeight: fontWeights[1],
    color: colors.primary.dark,
    fontFamily: configFont,
  },
  caption: {
    fontSize: fontSize[1],
    fontWeight: fontWeights[0],
    fontFamily: configFont,
    color: colors.primary.main,
    cursor: `pointer`,
    '&:hover': {
      color: colors.primary.dark,
      textDecoration: `underline`,
    },
  },
}
theme.overrides = {
  MuiTableHead: {
    root: {
      backgroundColor: colors.gray[1],
      border: `2px solid #E6EAEE`,
    },
  },
  MuiTableCell: {
    head: {
      color: theme.palette.primary.main,
      fontSize: fontSize[1],
      fontWeight: fontWeights[2],
    },
    root: {
      color: theme.palette.primary.main,
      display: `table-cell`,
      padding: `16px`,
      fontSize: fontSize[0],
      textAlign: `left`,
      fontFamily: `Chilena`,
      fontWeight: `400`,
      verticalAlign: `inherit`,
    },
    body: {
      color: theme.palette.primary.main,
    },
  },
  MuiAccordion: {
    root: {
      width: `100%`,
      margin: theme.spacing(0, 0, 3, 0),
      padding: theme.spacing(1, 4),
      borderRadius: 10,
      boxShadow: `12px 8px 44px -12px rgba(127, 143, 164, 0.2)`,
      '&$expanded': {
        margin: theme.spacing(0, 0, 3, 0),
      },
      '&::before': {
        content: `none`,
      },
      '&$disabled': {
        opacity: 0.7,
        backgroundColor: colors.primary.light,
      },
    },
  },
  MuiAccordionSummary: {
    root: {
      minHeight: 0,
      height: `auto`,
      padding: 0,
      borderBottom: `1px solid ${colors.grayRgba[4]}`,
      marginBottom: theme.spacing(1),
      '&$expanded': {
        minHeight: `0`,
        margin: `0`,
      },
      '&$disabled': {
        opacity: 0.7,
        backgroundColor: colors.primary.light,
      },
    },
    content: {
      padding: theme.spacing(0.5, 0),
      margin: 0,
      '&$expanded': {
        margin: `0`,
      },
    },
    expandIcon: {
      paddingBottom: theme.spacing(0.8),
    },
  },
  MuiAccordionDetails: {
    root: {
      padding: theme.spacing(2, 0, 5, 0),
      display: `flex`,
      justifyContent: `flex-start`,
      alignContent: `flex-start`,
      alignItems: `flex-start`,
      flexWrap: `wrap`,
      width: `100%`,
      '&$disabled': {
        opacity: 0.7,
        backgroundColor: colors.primary.light,
      },
    },
  },
  MuiTabs: {
    root: {
      minHeight: `unset`,
      '&$disabled': {
        opacity: 0.7,
        backgroundColor: colors.primary.light,
      },
    },
    indicator: {
      background: colors.primary.light,
      height: 3,
      width: `200px !important`,
    },
    flexContainer: {
      alignItems: `flex-end`,
      width: `100%`,
    },
  },
  MuiTab: {
    root: {
      padding: theme.spacing(1, 0, 0.5, 0),
      borderTopLeftRadius: 8,
      minWidth: 100,
      borderTopRightRadius: 8,
      minHeight: `unset`,
      background: `linear-gradient(0deg, #F4F7FA 0%, #FFFFFF 100%)`,
      border: `1px solid #E6EAEE`,
      marginRight: theme.spacing(1),
      '&$disabled': {
        opacity: 0.7,
        backgroundColor: colors.primary.light,
      },
    },
    textColorPrimary: {
      color: colors.primary.main,
    },
    selected: {
      border: `1px solid ${theme.palette.primary.light}`,
      minWidth: 200,
      background: colors.primary.light,
      transition: `.3s`,
      '&:hover': {
        background: colors.primary.light,
        opacity: 0.9,
      },
    },
    wrapper: {
      display: `flex`,
      justifyContent: `flex-start`,
      flexDirection: `row`,
      paddingBottom: theme.spacing(1),
      paddingLeft: theme.spacing(4),
    },
  },
  MuiStepper: {
    root: {
      width: `100%`,
      backgroundColor: theme.palette.background.default,
      padding: `24px 0px`,
    },
  },
  MuiStepLabel: {
    label: {
      fontSize: fontSize[1],
    },
  },
  MuiStepIcon: {
    root: {
      color: `${colors.ternary.dark}`,
      borderRadius: `50%`,
      width: `49px`,
      height: `49px`,
    },
    active: {
      color: `${colors.ternary.dark} !important`,
      border: `2px solid ${colors.secondary.main}`,
    },
    text: {
      fill: colors.secondary.main,
      fontWeight: fontWeights[2],
      transform: `translateY(1px)`,
    },
  },
  MuiDrawer: {
    paperAnchorTop: {
      top: 0,
      left: 0,
      right: 0,
      bottom: `auto`,
      height: `auto`,
      maxHeight: `100%`,
      marginTop: `22%`,
    },
  },
  MuiInputAdornment: {
    root: {
      '& > p': {
        color: `${colors.primary.main}`,
      },
    },
  },

  MuiStep: {
    alternativeLabel: {},
    completed: {},
  },
  MuiSnackbar: {
    root: {
      cursor: `pointer`,
      borderRadius: 15,
      color: colors.primary.light,
      fontWeight: fontWeights[3],
      transition: `0.3s`,
      '&:hover': {
        opacity: 0.6,
      },
    },
  },
  MuiStepConnector: {
    line: {
      borderColor: colors.secondary.main,
    },
    alternativeLabel: {
      color: `red`,
      top: `25px`,
      left: `calc(-50% + 25px)`,
      right: `calc(50% + 25px)`,
    },
  },
  MuiTypography: {
    gutterBottom: {
      marginBottom: `0.5em`,
    },
  },
  MuiDivider: {
    light: {
      backgroundColor: colors.gray[2],
      opacity: `0.8`,
    },
  },
  MuiCircularProgress: {
    colorPrimary: {
      color: theme.palette.primary.main,
    },
  },
  MuiTextField: {
    root: {
      border: `none`,
      height: `auto`,
      width: `100%`,
    },
  },
  MuiInputBase: {
    root: {
      overflow: `hidden`,
      borderRadius: 5,
      '&$disabled': {
        background: `transparent`,
      },
    },
    input: {
      padding: theme.spacing(1, 0, 1, 1),
      '&:disabled': {
        opacity: `initial`,
        backgroundColor: `lightgrey`,
        color: theme.palette.primary.main,
      },
    },
    inputAdornedStart: {
      padding: `17px 2px`,
    },
    adornedStart: {
      paddingLeft: theme.spacing(2.75),
      backgroundColor: theme.palette.primary.light,
    },
    adornedEnd: {
      paddingRight: theme.spacing(1),
    },
    multiline: {
      padding: `17px 22px`,
    },
    inputMarginDense: {
      paddingTop: `8px`,
      paddingBottom: `8px`,
    },
  },
  MuiInput: {
    root: {
      border: `1px solid #7F8FA4`,
      borderRadius: 5,
      fontSize: fontSize[1],
      width: `100%`,
      '&$focused': {
        background: colors.primary.light,
        outline: `none !important`,
        border: `1px solid ${colors.success.main}`,
        borderRadius: 5,
      },
      '&.validInput': {
        border: `1px solid ${colors.success.main}`,
        borderRadius: 5,
      },
      '&$disabled': {
        border: `none`,
        background: `transparent`,
      },
    },
    input: {
      background: colors.primary.light,
      textOverflow: `ellipsis`,
      color: `black`,
      outline: `none !important`,
      borderRadius: 5,
      '&$disabled': {
        background: `transparent`,
      },
    },
    underline: {
      '&:before': {
        content: ``,
      },
      '&:after': {
        content: ``,
      },
    },
    error: {
      border: `1.2px solid ${colors.error.main}`,
    },
  },
  MuiMenuItem: {
    root: {
      fontSize: 12,
    },
  },
  MuiListItem: {
    selected: {},
    root: {},
    button: {
      '&:hover': {
        backgroundColor: colors.ternary.light,
      },
    },
  },
  MuiLinearProgress: {
    root: {
      borderRadius: `5px`,
    },
  },
  MuiRadio: {
    root: {
      paddingTop: 2,
      paddingBottom: 2,
    },
    colorPrimary: {
      color: colors.gray[2],
      '&$checked': {
        color: colors.success.main,
        '&$disabled': {
          color: colors.success.main,
        },
      },
    },
  },
  MuiFormControl: {
    marginNormal: {
      marginTop: `0`,
    },
  },
  MuiFormControlLabel: {
    root: {
      fontSize: `${fontSize[1]} !important`,
    },
  },
  MuiAutocomplete: {
    root: { border: `none`, backgroundColor: `initial` },
    inputRoot: {
      height: `55px`,
      padding: `0 !important`,
    },
  },
  MuiToolbar: {
    gutters: {
      paddingLeft: `0px !important`,
      paddingRight: `0px !important`,
    },
  },
  MuiTooltip: {
    tooltip: {
      color: `white`,
      backgroundColor: `black`,
      fontSize: 14,
    },
  },
  MuiAppBar: {
    colorDefault: {
      backgroundColor: `white`,
    },
    colorPrimary: {
      color: `white`,
      backgroundColor: colors.primary.light,
      padding: theme.spacing(0),
    },
  },
  MuiSvgIcon: {
    root: {
      width: `1.2rem`,
      height: `1.2rem`,
    },
    colorPrimary: {
      color: colors.ternary.main,
    },
  },
  MuiAlert: {
    root: {
      maxWidth: `330px`,
    },
    message: {
      fontSize: fontSize[1],
      fontWeight: fontWeights[0],
    },
    filledSuccess: {
      color: `#ffffff`,
      fontWeight: 500,
      backgroundColor: '#28a745',
    },
  },
  MuiCheckbox: {
    colorPrimary: {
      color: colors.gray[2],
      '&$checked': {
        color: colors.success.main,
      },
    },
    colorSecondary: {
      color: colors.gray[3],
      width: `22px`,
      height: `22px`,
      '&$checked': {
        color: colors.primary.main,
      },
    },
  },
  MuiButton: {
    label: {
      fontSize: fontSize[1],
      fontWeight: fontWeights[1],
      textTransform: `none`,
      [theme.breakpoints.down(`sm`)]: {
        fontSize: fontSize[0],
      },
    },
    root: {
      padding: theme.spacing(1, 2),
      borderRadius: 10,
      minWidth: 200,
      maxWidth: 250,
      lineHeight: 1.5,
    },
    containedPrimary: {
      backgroundColor: colors.success.main,
      border: `2px solid ${colors.success.main}`,
      '&:hover': {
        backgroundColor: colors.gray[0],
        color: colors.success.main,
      },
    },
    containedSecondary: {
      color: colors.success.main,
      border: `2px solid ${colors.success.main}`,
      backgroundColor: colors.gray[0],
      '&:hover': {
        backgroundColor: colors.success.main,
        color: colors.primary.light,
      },
    },
  },
  MuiIconButton: {
    root: {
      '&:hover': {
        backgroundColor: `transparent`,
      },
    },
    colorPrimary: {
      fill: `#ffffff`,
      '&:hover': {
        fill: colors.success.main,
      },
    },
  },
  MuiSelect: {
    root: {
      padding: theme.spacing(1, 0, 1, 1),
    },
    select: {
      paddingRight: `0 !important`,
      borderRadius: 8,
      '&.validInput': {
        border: `1px solid ${colors.success.main}`,
        borderRadius: 5,
      },
    },
    icon: {
      marginRight: theme.spacing(1),
    },
  },
  MuiChip: {
    root: {
      backgroundColor: `#E5F1FF`,
      margin: theme.spacing(0.5),
      color: colors.success.main,
    },
    deleteIcon: {
      fill: colors.ternary.dark,
      '&:hover': {
        fill: colors.primary.light,
      },
    },
  },
  MuiPickersDay: {
    day: {
      color: theme.palette.primary.main,
    },
  },
  MuiTablePagination: {
    root: {
      color: colors.gray[3],
    },
  },
}
export default theme
